import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from '../api/axios';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const savedCompany = localStorage.getItem('company');
    
    if (token && savedCompany) {
      // Set the company immediately for faster UI rendering
      setCompany(JSON.parse(savedCompany));
      
      // Verify token is still valid in the background
      axios.get('/user')
        .then(response => {
          // Update with latest company data
          setCompany(response.data.company);
          localStorage.setItem('company', JSON.stringify(response.data.company));
        })
        .catch((error) => {
          console.error('Token validation failed:', error.response?.status, error.response?.data);
          // If token validation fails, clear storage
          localStorage.removeItem('token');
          localStorage.removeItem('company');
          setCompany(null);
          // Only redirect if not already on login/register pages
          if (window.location.pathname !== '/login' && window.location.pathname !== '/register') {
            window.location.href = '/login';
          }
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const register = async (data) => {
    const response = await axios.post('/register', data);
    const { company, token } = response.data;
    
    localStorage.setItem('token', token);
    localStorage.setItem('company', JSON.stringify(company));
    setCompany(company);
    
    return response.data;
  };

  const login = async (email, password) => {
    const response = await axios.post('/login', { email, password });
    const { company, token } = response.data;
    
    localStorage.setItem('token', token);
    localStorage.setItem('company', JSON.stringify(company));
    setCompany(company);
    
    return response.data;
  };

  const logout = async () => {
    try {
      await axios.post('/logout');
    } catch (error) {
      console.error('Logout error:', error);
    }
    
    localStorage.removeItem('token');
    localStorage.removeItem('company');
    setCompany(null);
  };

  const refreshCompany = async () => {
    const response = await axios.get('/user');
    setCompany(response.data.company);
    localStorage.setItem('company', JSON.stringify(response.data.company));
  };

  const value = {
    company,
    loading,
    register,
    login,
    logout,
    refreshCompany,
    isAuthenticated: !!company,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

